//@ts-check

/** @typedef {import("./game_lists/GameList").GameListType} GameListType */
/** @typedef {import("./game_lists/GameList").GameType} GameType */

import { findGame, isMobile, safeCreateUrl } from "./Utils"


/**
 * @typedef {Object} Parameter
 * @property {string} backendName
 * @property {string=} frontendName
 * @property {boolean=} required
 * @property {(value: string, gameList: GameListType, urlParams: URLSearchParams) => string} [specialParsing]
 */

/**
 * 1. if just backendName is provided, frontendName is the same as backendName, and the url value is passed as is
 * 2. if frontendName is provided, the url value is passed as is, but with differentName, for example, backend sends gameId=123, frontend receives gameID=123
 * 3. if specialParsing is provided, the url value is passed to the function, and the result is used as the parameter value
 * 4. if required is provided, the parameter is required, and an error is thrown if it is not provided
 * 5. check ReturnFrontendParameters below for implementation details
 * @type {Parameter[]}
 */
const E3Parameters = [
    {
        backendName: "gameId",
        frontendName: "gameName",
        required: true,
        specialParsing: (value, gameList) => {
            const gameName = /** @type {GameType} */(findGame(value, gameList)).name
            const gameVersion = checkIfMobile() ? "_mobile" : "_web"
            return gameName + gameVersion
        }
    },
    {
        backendName: "userId",
        frontendName: "playerID",
        required: true
    },
    {
        backendName: "sessionId",
        frontendName: "sessionID",
        required: true
    },
    {
        backendName: "gameServerAddress",
        frontendName: "secureConnection",
        specialParsing: (value) => String(safeCreateUrl(value).protocol !== "http:")
    },
    {
        backendName: "gameServerAddress",
        frontendName: "host",
        required: true,
        specialParsing: (value) => {
            const host = safeCreateUrl(value)
            return host.hostname + host.pathname
        }
    },
    {
        backendName: "integrations",
        frontendName: "returnURL",
        specialParsing: (value) => encodeURIComponent(value)
    },
    {
        backendName: "language",
        frontendName: "languages",
        specialParsing: (value) => value ? value : "en" //huh that doesn't make sense -> it's not a required param anyway
    },
    {
        backendName: "inWebJackpots",
        specialParsing: () => "" //just a flag, no value
    },
    {
        backendName: "debug",
    },
    {
        backendName: "gameMode",
    },
    {
        backendName: "operatorId",
    },
    {
        backendName: "gameId", //used here again, but we use it in providerGameId too
        frontendName: "providerGameId",
        specialParsing: (value, gameList, urlParams) => {
            if (urlParams.get("addOperatorIdToGameId") === "true") {
                const originalId = findGame(value, gameList)?.originalId;
                value = originalId || value
            }
            return "BFG" + value;
        }
    },
    {
        backendName: "limitBuyFeatureToMaxBet"
    },
    {
        backendName: "jackpotSessionId",
    },
    {
        backendName: "rciframeurl",
        specialParsing: (value) => encodeURIComponent(value)
    },
    {
        backendName: "rchistoryurl",
        specialParsing: (value) => encodeURIComponent(value)
    },
    {
        backendName: "homeurl",
        specialParsing: (value) => encodeURIComponent(value)
    },
    {
        backendName: "rcinterval",
        specialParsing: (value) => encodeURIComponent(value)
    },
    {
        backendName: "rcelapsed",
        specialParsing: (value) => encodeURIComponent(value)
    },
    {
        backendName: "showrgdebuglog",
        specialParsing: (value) => encodeURIComponent(value)
    },
    {
        backendName: "rcenable",
        specialParsing: (value) => encodeURIComponent(value)
    },
    {
        backendName: "extendPlaytechAPISupport"
    },
    {
        backendName: "stage"
    },
    {
        backendName: "jackpotRulesTemplateUrl"
    },
    {
        backendName: "jackpotRulesUrl"
    },
    {
        backendName: "jackpotExtraRulesUrl"
    },
    {
        backendName: "showLowerBetNotification"
    },
    {
        backendName: "hackRulesMaxWinningsCap"
    },
    {
        backendName: "showMaxHourlyLossRules"
    },
    {
        backendName: "exitButtonEnabledInIframes"
    },
    {
        backendName: "skipRevertWinningsPresentation"
    },
    {
        backendName: "showPaytableAtGameStart"
    },
    {
        backendName: "enableRubyJokerBetPolyfill"
    },
    {
        backendName: "showTotalAutoplayCost"
    },
    {
        backendName: "showAutoplayConfirmationWindow"
    }
]

/** @type {Parameter[]} */
const E2Parameters = [
    {
        backendName: "gameId",
        required: true
    },
    {
        backendName: "userId",
        required: true
    },
    {
        backendName: "sessionId",
        required: true
    },
    {
        backendName: "gameServerAddress",
        frontendName: "host",
        required: true,
        specialParsing: (value) => {
            const host = safeCreateUrl(value)
            return host.hostname + host.pathname
        }
    },
    {
        backendName: "useNovoLoader",
        specialParsing: (value) => value === "true" || value === "" ? "true" : "false" //not sure if it comes like this &useNovoLoader=true or just &useNovoLoader tbh
    },
    {
        backendName: "operatorId",

    },
    {
        backendName: "providerGameId",
        specialParsing: (value) => "BFG" + value
    },
    {
        backendName: "jackpotSessionId",
    }
]

/**
 * 
 * @param {URLSearchParams} urlParams 
 * @param {"e3" | "e2"} engine 
 * @param {GameListType} gameList 
 * @returns {string}
 */
export function ReturnFrontendParametersString(urlParams, engine, gameList) {
    let paramString = ""
    const selectedParameters = engine === "e3" ? E3Parameters : E2Parameters
    selectedParameters.forEach((param) => {
        const backendValue = urlParams.get(param.backendName)
        if (backendValue !== null && backendValue !== undefined) {
            const frontendName = param.frontendName || param.backendName
            const value = param.specialParsing ? param.specialParsing(backendValue, gameList, urlParams) : backendValue
            if (value !== "") {
                paramString += `&${frontendName}=${value}`
            } else {
                paramString += `&${frontendName}`
            }
        } else if (param.required) {
            throw new Error(`Missing required parameter: ${param.backendName}`)
        }
    })
    //remove first "&"
    return paramString.substring(1)
}

/**
 * checks if forceMobile is included or if the user agent seems to me mobile
 * @returns {boolean}
 */
export const checkIfMobile = () => {
    let runMobile = false;
    if (typeof document !== 'undefined') {
        runMobile = document.location.search.includes("forceMobile") || isMobile(navigator?.userAgent, window?.innerWidth || 1, window?.innerHeight || 1);
    }
    return runMobile;
}
